import Bugsnag from "@bugsnag/js";
import { client } from "@nju/http-client";
import type { ISetDeliveryAddressMode } from "../types";
import type {
  IDeliveryMultiOrderItems,
  IMultiCreateDeliveryAddressResult,
} from "./types";
import { serverResponse } from "./validators";

interface ICreateDeliveryAddressProps {
  orchestratorId: string;
  mode: ISetDeliveryAddressMode;
  delivery?: {
    firstName: string;
    lastName: string;
    street: string;
    streetNumber: string;
    apartmentNumber: string;
    postCode: string;
    municipality: string;
    country: string;
    contactNumber: string;
    note: string | undefined;
    community: string | undefined;
    district: string | undefined;
    stateOrProvince: string | undefined;
    CBSReference?: {
      communityId?: number;
      districtId?: number;
      municipalityId?: number;
      stateOrProvinceId?: number;
      streetId?: number;
      countryId: number;
    };
  };
  multiOrderItems?: IDeliveryMultiOrderItems[];
  isSales: boolean;
}

export async function multiCreateDeliveryAddress({
  orchestratorId,
  delivery,
  mode,
  multiOrderItems,
  isSales,
}: ICreateDeliveryAddressProps): Promise<IMultiCreateDeliveryAddressResult> {
  const actions = {
    SET: "DELIVERY_METHOD_SET",
    CHANGE: "DELIVERY_METHOD_UPDATE",
  } as Record<ISetDeliveryAddressMode, string>;

  const deliveryObject = delivery && {
    firstName: delivery.firstName,
    lastName: delivery.lastName,
    street: delivery.street,
    streetNumber: delivery.streetNumber,
    apartmentNumber: delivery.apartmentNumber,
    postCode: delivery.postCode,
    municipality: delivery.municipality,
    country: delivery.country,
    contactNumber: delivery.contactNumber,
    ...(delivery.note && { note: delivery.note }),
    ...(delivery.apartmentNumber && {
      apartmentNumber: delivery.apartmentNumber,
    }),
    community: delivery.community,
    district: delivery.district,
    stateOrProvince: delivery.stateOrProvince,
    CBSReference: delivery.CBSReference,
  };

  const result = await client(
    `${process.env.NEXT_PUBLIC_PUBLIC_GATEWAY_HOST}/orchestrators/${orchestratorId}/actions/${actions[mode]}`,
    {
      method: "post",
      validator: serverResponse,
      json: {
        ...(isSales && { deliveryTypeId: "COURIER" }),
        ...(deliveryObject && { delivery: deliveryObject }),
        ...(multiOrderItems && { multiOrderItems }),
      },
      headers: {
        version: "2",
        apikey: process.env.NEXT_PUBLIC_APIKEY || "unknown",
      },
    },
  );

  if (result.isError) {
    if (result.err.code !== "MISSING_PROPERTY") {
      Bugsnag.notify(result.err);
    }

    return result;
  }

  return result;
}
